import { call, put } from 'redux-saga/effects';

import { takeLatest } from '../../../utils/reduxSaga';
import { OUTSIDE_API } from '../../../consts/endpoints';
import { actions as sagaActions, UPDATE_TELEPHONY_USER } from './types';
import { fetchUsers } from '../../../pages/telephonyUsers/actions/getUsers';
import RESPONSE_CODES from '../../../consts/responseCodes';
import { updateData } from '../../../utils/http';
import { actions as reduxActions } from '../reducers';
import showSnackbar from '../../../utils/showSnackbar';
import { actions as groupsReduxActions } from '../../../pages/groups/reducers';
import { isObject, isString, isUndefined } from '../../../utils/isDefined';

export function updateTelephonyUserOnSubmit({
  userData,
  clientConnectId,
  clientId,
  mitelUserId,
  values,
  isGeneralData,
  fromUsersTable,
  fromGroupsTable,
  groupsOnly,
  closeOnSuccess
}) {
  return {
    type: sagaActions[UPDATE_TELEPHONY_USER],
    userData,
    clientConnectId,
    clientId,
    mitelUserId,
    values,
    isGeneralData,
    fromUsersTable,
    fromGroupsTable,
    groupsOnly,
    closeOnSuccess
  };
}

export function* updateTelephonyUser({
  userData,
  clientConnectId,
  clientId,
  mitelUserId,
  values,
  isGeneralData,
  fromUsersTable = true,
  fromGroupsTable = false,
  groupsOnly = false,
  closeOnSuccess = false
}) {
  const mappedUserData = !groupsOnly ? mapUserDataToUpdate(userData) : userData;

  try {
    const response = yield call(updateData, {
      url: `${OUTSIDE_API.CLIENTS}/${clientConnectId}/users/${mitelUserId}`,
      data: { user: { ...mappedUserData } }
    });

    if (response.status === RESPONSE_CODES.NO_CONTENT) {
      yield call(showSnackbar, {
        intlTextKey: 'MESSAGE.UPDATE.USER',
        messageType: 'success'
      });
    }

    if (fromUsersTable) {
      yield call(fetchUsers, { connectClientId: clientConnectId, clientId });
    }

    if (fromGroupsTable) {
      yield put(groupsReduxActions.resetGroupUsers());
    }

    if (closeOnSuccess) {
      yield put(reduxActions.resetUser());
    } else {
      yield put(reduxActions.updateUser(values, isGeneralData));
    }
  } catch {}
}

function* watchUpdateTelephonyUser() {
  yield takeLatest(sagaActions[UPDATE_TELEPHONY_USER], updateTelephonyUser);
}

export default watchUpdateTelephonyUser();

const mapUserDataToUpdate = userData => {
  const { phone_numbers, ...rest } = userData;

  const phonesObjectKeys = Object.keys(phone_numbers || {});

  const mappedNumbers = phonesObjectKeys.reduce((arr, key) => {
    if (
      !isObject(phone_numbers[key]) ||
      Object.keys(phone_numbers[key]).length === 0
    ) {
      return arr;
    }

    const value = phone_numbers[key].value;

    if (isUndefined(value) || value === '') {
      return arr;
    }

    const aliases = phone_numbers[key]?.aliases || [];

    const mappedAliases = aliases.reduce((arr, alias) => {
      if (!isString(alias.value) || alias.value.trim() === '') {
        return arr;
      }
      const { id, ...aliasWithoutId } = alias;
      return [...arr, aliasWithoutId];
    }, []);

    const getLineType = lineKey => {
      switch (lineKey) {
        case 'primaryLine':
        case 'primary_line':
          return 'PRIMARY';
        case 'secondaryLine':
        case 'secondary_line':
          return 'SECONDARY';
        case 'faxLine':
        case 'fax_line':
          return 'FAX';
        default:
          return null;
      }
    };

    const lineType = getLineType(key);

    if (!lineType) {
      return arr;
    }

    return [
      ...arr,
      {
        ...phone_numbers[key],
        aliases: mappedAliases,
        type: lineType,
        mobile_vpn: phone_numbers[key].mobile_vpn || false
      }
    ];
  }, []);

  return {
    ...rest,
    phone_numbers: mappedNumbers
  };
};
